<template>
  <div class="bg">
    <template v-if="total">
      <div class="bg-top"></div>
      <div class="bg-bottom"></div>
    </template>
    <div class="template-center-page">
      <div class="title">{{ total || loading ? '版本中心' : '敬请期待' }}</div>
      <template v-if="total">
        <div class="table">
          <div v-for="item of tableData" :key="item.id" class="template">
            <div v-if="showLatest(item)" class="latest tag">最新</div>
            <div v-if="item.basicState === TEMPLATE_STATE.RESERVATION" class="tag reservation">敬请期待</div>
            <a class="cover-container" :href="'/templateCenter/detail?id=' + item.id" target="_blank">
              <img class="cover" :src="joinFileUrl(item.coverUrl)" />
            </a>
            <div class="content">
              <div class="template-title">{{ item.basicName }}</div>
              <div class="update-time">更新时间：{{ dayjs(item.updateTime).format('YYYY-MM-DD') }}</div>
            </div>
            <div
              v-if="item.basicState === TEMPLATE_STATE.RESERVATION"
              :class="{ 'expectation-btn': true, expected: store.todayExpectedTemplate.includes(item.id) }"
              @click="expect(item.id)"
            >
              <div :id="'message-' + item.id" class="expect-success-message">
                <img src="/images/success.svg" class="success-icon" />
                {{ store.todayExpectedTemplate.includes(item.id) ? '期待成功' : '取消期待成功' }}
              </div>
              <img src="/images/templateCenter/expectation.png" class="icon" />
              {{ item.expectation }}
            </div>
          </div>
        </div>
        <t-pagination
          v-model:current="form.pageNum"
          :pageSize="form.pageSize"
          :total="total"
          :showPageSize="false"
          :totalContent="false"
          :onChange="getList"
        />
      </template>
      <img v-if="!total && !loading" src="/images/templateCenter/empty.svg" class="empty" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onUnmounted } from '#imports'
import { getTemplateList, type Template, getEngineType, expectTemplate } from '@/api'
import { joinFileUrl } from '@/utils'
import dayjs from 'dayjs'
import { TEMPLATE_STATE } from '@/enums'
import useStore from '@/stores/defaultStore'
import useGoMobile from '@/hooks/useGoMobile'

useGoMobile()
const form = reactive({ pageNum: 1, pageSize: 20, engineTypeId: '' })
const tableData = ref<Template[]>([])
const total = ref(0)
const loading = ref(true)

function getList() {
  return getTemplateList(form)
    .then((res) => {
      tableData.value = res.records
      total.value = +res.totalCount
    })
    .finally(() => {
      loading.value = false
    })
}

async function initData() {
  const res = await getEngineType()
  if (!res?.[0]?.id) {
    return
  }
  form.engineTypeId = res[0].id
  getList()
}

function showLatest(item: Template) {
  if (item.basicState !== TEMPLATE_STATE.ENABLE) {
    return false
  }
  const now = dayjs().unix()
  const target = dayjs(item.updateTime).unix()
  return (now - target) / 86400 < 7
}

import.meta.client && initData()

const store = useStore()
let timer: number = null
let expectLoading = false

function expect(id: string) {
  if (expectLoading) {
    return
  }
  expectLoading = true
  let flag: 1 | -1
  let arr: string[] = []
  if (store.todayExpectedTemplate.includes(id)) {
    arr = store.todayExpectedTemplate.filter((item) => item !== id)
    flag = -1
  } else {
    arr = [...store.todayExpectedTemplate, id]
    flag = 1
  }
  expectTemplate({ id, flag })
    .then(getList)
    .then(() => {
      // 连续展示 toast 的时候，防止上一个定时器把下一个 toast 给关了
      clearTimeout(timer)
      store.setTodayExpectedTemplate(arr)
      document.getElementById('message-' + id).className = 'expect-success-message expect-success-message-show'
      timer = window.setTimeout(() => {
        document.getElementById('message-' + id).className = 'expect-success-message'
      }, 1000)
    })
    .finally(() => {
      expectLoading = false
    })
}

onUnmounted(() => {
  clearTimeout(timer)
})
</script>

<style lang="scss">
// 后续有新的需求用到分页，还是一样的样式的话，抽离成公共样式
.template-center-page {
  .t-pagination {
    justify-content: center;

    .t-pagination__number,
    .t-pagination__btn {
      border: 0;
      line-height: 32px;
      background: transparent;
      color: #ffffff;

      &.t-is-current {
        background: rgba(255, 255, 255, 0.1);
      }

      &.t-is-disabled {
        color: #000000;
      }
    }
  }
}
</style>

<style scoped lang="scss">
.bg {
  background: #141228;
  padding: 40px 0;
  min-width: var(--pc-page-min-width);
  position: relative;

  .bg-top {
    background-image: url('/images/templateCenter/bgTop.png');
    background-size: cover;
    width: 100vw;
    height: 600px;
    position: absolute;
    left: 0;
    top: 0;
  }

  .bg-bottom {
    background-image: url('/images/templateCenter/bgBottom.png');
    background-size: cover;
    width: 100vw;
    height: 480px;
    position: absolute;
    left: 0;
    bottom: 0;
  }
}

.template-center-page {
  position: relative;
  z-index: 1;
}

.title {
  color: #ffffff;
  font-weight: 500;
  font-size: 28px;
  background-image: url('/images/titleBg.svg');
  background-size: cover;
  width: 246px;
  height: 40px;
  margin: 0 auto 40px;
  text-align: center;
  line-height: 40px;
}

.table {
  display: grid;
  grid-template-columns: repeat(4, 312px);
  grid-template-rows: repeat(auto-fill, 254px);
  gap: 24px;
  width: 1320px;
  min-height: 750px;
  margin: 0 auto 48px;
}

.template {
  width: 312px;
  border-radius: 6px 6px 12px 12px;
  position: relative;

  .tag {
    position: absolute;
    right: 6px;
    top: 6px;
    height: 26px;
    padding: 0 10px;
    font-size: 13px;
    text-align: center;
    line-height: 26px;
    border-radius: 4px;
    z-index: 1;
  }

  .reservation {
    color: #ffffff;
    background: rgba(0, 0, 0, 0.8);
  }

  .latest {
    color: #764107;
    background: linear-gradient(91deg, rgba(255, 234, 214, 0.9) 0.75%, rgba(255, 220, 168, 0.9) 99.73%);
  }

  .cover-container {
    display: block;
    width: 100%;
    height: 175px;
    overflow: hidden;
    border-radius: 12px;

    .cover {
      width: 100%;
      height: 175px;
      border-radius: 12px;
      object-fit: cover;
      transition: all 0.3s;
    }
  }

  .content {
    padding: 12px;

    .template-title {
      color: #ffffff;
      font-size: 18px;
      font-weight: 500;
      line-height: 30px;
    }

    .update-time {
      color: rgba(255, 255, 255, 0.5);
      font-size: 14px;
      font-weight: 500;
      line-height: 24px; /* 150% */
    }
  }

  .expectation-btn {
    position: absolute;
    z-index: 1;
    right: 12px;
    bottom: 20px;
    display: flex;
    align-items: center;
    padding: 8px 12px;
    border-radius: 50px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.04);
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;

    .icon {
      width: 22px;
      height: 22px;
      margin-right: 4px;
    }
  }

  .expected {
    background: #050614;
  }

  .expect-success-message {
    position: absolute;
    top: -42px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 8px;
    background: #252343;
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
    padding: 0 12px;
    display: flex;
    align-items: center;
    font-size: 14px;
    height: 34px;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s;
    white-space: nowrap;

    .success-icon {
      width: 16px;
      height: 16px;
      margin-right: 4px;
    }
  }

  .expect-success-message-show {
    opacity: 1;
    visibility: visible;
  }

  &:hover {
    .cover {
      transform: scale(1.1);
    }
  }
}

.empty {
  width: 136px;
  height: 136px;
  object-fit: cover;
  margin: 0 auto;
  display: block;
}
</style>
